// 2026-09 R7 — pure helpers shared by the Pipelines board's Kanban and Table
// views, so the two can never disagree about which rows exist, how many there
// are, or what is read-only. No React, no I/O.

// Kanban: cards rendered per column before "Show more"; each click reveals
// another step. Counts always come from the full column, never the window.
export const COLUMN_WINDOW = 50;
// Table: rows rendered before "Show more".
export const TABLE_WINDOW = 100;
// One page of the ACTIVE board (server default page size).
export const ACTIVE_PAGE_SIZE = 500;
// Column that catches an active row whose stage isn't a column on this board
// (a legacy / unmapped name), so a Kanban card is never silently dropped while
// the Table still lists it.
export const UNPLACED_COLUMN = "Other / unmapped stage";

const nf = new Intl.NumberFormat("en-US");

// "1,234" — or "5,000+" when the server capped the count.
export const formatCount = (n, capped = false) => `${nf.format(Number(n) || 0)}${capped ? "+" : ""}`;

// A per-column badge: exact when every active row is loaded, "N+" when more
// active rows exist on the server than the board has loaded so far.
export const columnCountLabel = (loaded, moreActiveRows) => `${nf.format(loaded)}${moreActiveRows ? "+" : ""}`;

// The next follow-up shown on both views: the row's own next_followup_at when
// present, else the canonical Next Action's due date the grid actually returns.
export const nextFollowUpAt = (r) => r?.next_followup_at || r?.next_action?.due_at || null;

// The window of a list: the first `shown` items plus how many remain hidden.
export function windowed(items, shown) {
  const list = items || [];
  const n = Math.max(0, shown | 0);
  return { visible: list.slice(0, n), hidden: Math.max(0, list.length - n) };
}

// Rows no active column claims (byStage keys that aren't a board column).
export function unplacedRows(stages, byStage) {
  const names = new Set((stages || []).filter((s) => s.active !== false).map((s) => s.name));
  return Object.entries(byStage || {}).filter(([k]) => !names.has(k)).flatMap(([, v]) => v);
}

// One summary for both views: total loaded rows, per-column counts, unplaced.
export function summarizeBoard(stages, byStage) {
  const perStage = {};
  (stages || []).filter((s) => s.active !== false).forEach((s) => {
    perStage[s.name] = (byStage?.[s.name] || []).length;
  });
  const unplaced = unplacedRows(stages, byStage).length;
  const placed = Object.values(perStage).reduce((a, b) => a + b, 0);
  return { perStage, unplaced, placed, total: placed + unplaced };
}
